import { useMemo, useState } from "react";
import {
  Alert, Box, Button, Collapse, Dialog, DialogActions, DialogContent, FormControlLabel,
  IconButton, List, MenuItem, Paper, Stack, Switch,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, Tooltip, Typography,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { accountsApi, type AccountGroupNode, type AccountBalanceRow, type AccountTypeName } from "@/api/endpoints";
import { errorMessage } from "@/api/client";
import { useAuth } from "@/store/auth";
import { PageHeader } from "@/components/PageHeader";
import { StatusPill } from "@/components/StatusPill";
import { TableSkeleton } from "@/components/Skeletons";
import { ClosableDialogTitle } from "@/components/ClosableDialogTitle";

const inr = new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const ACCOUNT_TYPES: AccountTypeName[] = ["Asset", "Liability", "Equity", "Income", "Expense"];

/** Guess the account type for a new account from the root group it lives under. */
function typeFromName(name: string): AccountTypeName {
  const n = name.toUpperCase();
  if (n.includes("LIABILIT")) return "Liability";
  if (n.includes("INCOME") || n.includes("REVENUE")) return "Income";
  if (n.includes("EXPENSE") || n.includes("EXPENDITURE")) return "Expense";
  if (n.includes("EQUIT") || n.includes("CAPITAL") || n.includes("FUND")) return "Equity";
  return "Asset";
}

type GroupDialogState = { mode: "create" | "edit"; parentId: string | null; node?: AccountGroupNode };
type AccountDialogState = { mode: "create" | "edit"; groupId: string; defaultType: AccountTypeName; row?: AccountBalanceRow };

export function ChartOfAccountsPage() {
  const qc = useQueryClient();
  const canManage = useAuth(s => s.hasPermission("accounting.account.manage"));

  const tree = useQuery({ queryKey: ["coa-tree"], queryFn: () => accountsApi.groupTree() });
  const [selected, setSelected] = useState<AccountGroupNode | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const accounts = useQuery({
    queryKey: ["coa-accounts", selected?.id],
    queryFn: () => accountsApi.byGroup(selected!.id),
    enabled: !!selected,
  });

  // parent → children, and id → node
  const { childrenOf, byId, roots } = useMemo(() => {
    const nodes = tree.data ?? [];
    const byId = new Map(nodes.map(n => [n.id, n]));
    const childrenOf = new Map<string | null, AccountGroupNode[]>();
    for (const n of nodes) {
      const key = n.parentId ?? null;
      if (!childrenOf.has(key)) childrenOf.set(key, []);
      childrenOf.get(key)!.push(n);
    }
    return { childrenOf, byId, roots: childrenOf.get(null) ?? [] };
  }, [tree.data]);

  const rootTypeOf = (node: AccountGroupNode): AccountTypeName => {
    let cur: AccountGroupNode | undefined = node;
    while (cur?.parentId) cur = byId.get(cur.parentId);
    return typeFromName(cur?.name ?? node.name);
  };
  const breadcrumbFor = (node: AccountGroupNode): string[] => {
    const path: string[] = [];
    let cur: AccountGroupNode | undefined = node;
    while (cur) { path.unshift(cur.name); cur = cur.parentId ? byId.get(cur.parentId) : undefined; }
    return ["Accounting", "Chart of accounts", ...path];
  };

  const toggle = (id: string) =>
    setExpanded(prev => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const [groupDialog, setGroupDialog] = useState<GroupDialogState | null>(null);
  const [accountDialog, setAccountDialog] = useState<AccountDialogState | null>(null);
  const [viewRow, setViewRow] = useState<AccountBalanceRow | null>(null);

  const refetchAll = () => {
    qc.invalidateQueries({ queryKey: ["coa-tree"] });
    qc.invalidateQueries({ queryKey: ["coa-accounts"] });
  };

  const deleteGroup = useMutation({
    meta: { successMessage: "Group deleted" },
    mutationFn: (id: string) => accountsApi.deleteGroup(id),
    onSuccess: (_d, id) => { if (selected?.id === id) setSelected(null); refetchAll(); },
  });
  const deleteAccount = useMutation({
    meta: { successMessage: "Account deleted" },
    mutationFn: (id: string) => accountsApi.deleteAccount(id),
    onSuccess: () => refetchAll(),
  });

  const renderNode = (node: AccountGroupNode, depth: number) => {
    const kids = childrenOf.get(node.id) ?? [];
    const open = expanded.has(node.id);
    const isSel = selected?.id === node.id;
    return (
      <Box key={node.id}>
        <Box
          sx={{
            display: "flex", alignItems: "center", gap: 0.5, pr: 1,
            pl: 1 + depth * 2, py: 0.5, borderRadius: 1.5, cursor: "pointer",
            bgcolor: isSel ? "action.selected" : "transparent",
            "&:hover": { bgcolor: isSel ? "action.selected" : "action.hover" },
            "&:hover .coa-actions": { opacity: 1 },
          }}
          onClick={() => setSelected(node)}
        >
          <IconButton
            size="small" sx={{ p: 0.25, visibility: kids.length ? "visible" : "hidden" }}
            onClick={(e) => { e.stopPropagation(); toggle(node.id); }}
          >
            {open ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
          </IconButton>
          <Typography
            variant="body2"
            sx={{ flex: 1, minWidth: 0, fontWeight: depth === 0 ? 700 : isSel ? 600 : 500,
                  letterSpacing: depth === 0 ? 0.3 : 0, textTransform: depth === 0 ? "uppercase" : "none" }}
            noWrap
          >
            {node.name}
          </Typography>
          {node.accountCount > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5 }}>{node.accountCount}</Typography>
          )}
          {canManage && (
            <Stack direction="row" spacing={0} className="coa-actions"
              sx={{ opacity: 0, transition: "opacity 120ms" }} onClick={(e) => e.stopPropagation()}>
              <Tooltip title="Add sub-group"><IconButton size="small" color="primary"
                onClick={() => setGroupDialog({ mode: "create", parentId: node.id })}>
                <AddIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Rename group"><IconButton size="small"
                onClick={() => setGroupDialog({ mode: "edit", parentId: node.parentId ?? null, node })}>
                <EditIcon fontSize="small" /></IconButton></Tooltip>
              <Tooltip title="Delete group"><IconButton size="small" color="error"
                onClick={() => { if (confirm(`Delete group "${node.name}"?`)) deleteGroup.mutate(node.id); }}>
                <DeleteIcon fontSize="small" /></IconButton></Tooltip>
            </Stack>
          )}
        </Box>
        {kids.length > 0 && (
          <Collapse in={open} unmountOnExit>
            <List dense disablePadding>{kids.map(k => renderNode(k, depth + 1))}</List>
          </Collapse>
        )}
      </Box>
    );
  };

  const rows = accounts.data ?? [];

  return (
    <Box>
      <PageHeader
        breadcrumbs={["Accounting"]}
        title="Chart of accounts"
        subtitle="Account groups and their accounts, with posted balances."
        actions={
          canManage ? (
            <Button variant="contained" startIcon={<AddIcon />}
              onClick={() => setGroupDialog({ mode: "create", parentId: null })}>
              New group
            </Button>
          ) : undefined
        }
      />

      {(deleteGroup.isError || deleteAccount.isError) && (
        <Alert severity="error" sx={{ mb: 2 }}>{errorMessage(deleteGroup.error ?? deleteAccount.error)}</Alert>
      )}

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems="stretch">
        {/* ---- Tree ---- */}
        <Paper sx={{ width: { xs: "100%", md: 380 }, flexShrink: 0, p: 1, maxHeight: "76vh", overflow: "auto" }}>
          {tree.isLoading && <TableSkeleton rows={8} columns={1} />}
          {tree.data && roots.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>No account groups yet.</Typography>
          )}
          {roots.map(r => renderNode(r, 0))}
        </Paper>

        {/* ---- Detail ---- */}
        <Paper sx={{ flexGrow: 1, p: 2, minWidth: 0 }}>
          {!selected ? (
            <Box sx={{ p: 4, textAlign: "center" }}>
              <Typography color="text.secondary">Select a group to view its accounts.</Typography>
            </Box>
          ) : (
            <>
              <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={2} flexWrap="wrap">
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ letterSpacing: 0.6 }}>
                    {breadcrumbFor(selected).slice(2).join("  /  ").toUpperCase()}
                  </Typography>
                  <Typography variant="h6" fontWeight={700}>{selected.name}</Typography>
                </Box>
                {canManage && (
                  <Button variant="contained" size="small" startIcon={<AddIcon />}
                    onClick={() => setAccountDialog({ mode: "create", groupId: selected.id, defaultType: rootTypeOf(selected) })}>
                    New account
                  </Button>
                )}
              </Stack>

              <Box sx={{ mt: 1.5 }}>
                {accounts.isLoading ? <TableSkeleton rows={4} columns={5} /> : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell width={64}>Sl No.</TableCell>
                          <TableCell width={110}>Code</TableCell>
                          <TableCell>Account Name</TableCell>
                          <TableCell align="right" width={130}>Credit</TableCell>
                          <TableCell align="right" width={130}>Debit</TableCell>
                          <TableCell align="center" width={92}>Status</TableCell>
                          <TableCell align="right" width={120}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.map((a, i) => (
                          <TableRow key={a.id} hover>
                            <TableCell>{i + 1}</TableCell>
                            <TableCell sx={{ fontFamily: "monospace" }}>{a.code || "—"}</TableCell>
                            <TableCell>{a.name}</TableCell>
                            <TableCell align="right">{inr.format(a.credit)}</TableCell>
                            <TableCell align="right">{inr.format(a.debit)}</TableCell>
                            <TableCell align="center"><StatusPill label={a.isActive ? "Active" : "Inactive"} /></TableCell>
                            <TableCell align="right">
                              <Tooltip title="View"><IconButton size="small" onClick={() => setViewRow(a)}>
                                <VisibilityIcon fontSize="small" /></IconButton></Tooltip>
                              {canManage && <>
                                <Tooltip title="Edit"><IconButton size="small"
                                  onClick={() => setAccountDialog({ mode: "edit", groupId: selected.id, defaultType: a.accountType, row: a })}>
                                  <EditIcon fontSize="small" /></IconButton></Tooltip>
                                <Tooltip title="Delete"><IconButton size="small" color="error"
                                  onClick={() => { if (confirm(`Delete account "${a.name}"?`)) deleteAccount.mutate(a.id); }}>
                                  <DeleteIcon fontSize="small" /></IconButton></Tooltip>
                              </>}
                            </TableCell>
                          </TableRow>
                        ))}
                        {rows.length === 0 && (
                          <TableRow><TableCell colSpan={7}>
                            <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
                              No accounts in this group yet.
                            </Typography>
                          </TableCell></TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Box>
              {rows.length > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1.5 }}>
                  Showing <b>1–{rows.length}</b> of <b>{rows.length}</b> {rows.length === 1 ? "record" : "records"}
                </Typography>
              )}
            </>
          )}
        </Paper>
      </Stack>

      {groupDialog && (
        <GroupDialog
          state={groupDialog}
          parentName={groupDialog.parentId ? byId.get(groupDialog.parentId)?.name : undefined}
          onClose={() => setGroupDialog(null)}
          onSaved={() => { setGroupDialog(null); refetchAll(); }}
        />
      )}
      {accountDialog && (
        <AccountDialog
          state={accountDialog}
          onClose={() => setAccountDialog(null)}
          onSaved={() => { setAccountDialog(null); refetchAll(); }}
        />
      )}
      <ViewAccountDialog row={viewRow} onClose={() => setViewRow(null)} />
    </Box>
  );
}

// ---- Group create/edit dialog ----
function GroupDialog({ state, parentName, onClose, onSaved }: {
  state: GroupDialogState; parentName?: string; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = state.mode === "edit";
  const [code, setCode] = useState(state.node?.code ?? "");
  const [name, setName] = useState(state.node?.name ?? "");
  const save = useMutation({
    meta: { successMessage: isEdit ? "Group renamed" : "Group created" },
    mutationFn: () => isEdit
      ? accountsApi.updateGroup(state.node!.id, name.trim())
      : accountsApi.createGroup({ parentId: state.parentId, code: code.trim(), name: name.trim() }),
    onSuccess: onSaved,
  });
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <ClosableDialogTitle onClose={onClose}>{isEdit ? "Rename group" : "New account group"}</ClosableDialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {!isEdit && parentName && (
            <Typography variant="body2" color="text.secondary">Under <b>{parentName}</b></Typography>
          )}
          {!isEdit && (
            <TextField label="Code *" size="small" value={code} onChange={e => setCode(e.target.value)}
              helperText="Short, stable identifier (max 20 chars)" />
          )}
          <TextField label="Name *" size="small" value={name} onChange={e => setName(e.target.value)} autoFocus />
          {save.isError && <Alert severity="error">{errorMessage(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending || !name.trim() || (!isEdit && !code.trim())}
          onClick={() => save.mutate()}>{isEdit ? "Save" : "Create"}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- Account create/edit dialog ----
function AccountDialog({ state, onClose, onSaved }: {
  state: AccountDialogState; onClose: () => void; onSaved: () => void;
}) {
  const isEdit = state.mode === "edit";
  const [code, setCode] = useState(state.row?.code ?? "");
  const [name, setName] = useState(state.row?.name ?? "");
  const [accountType, setAccountType] = useState<AccountTypeName>(state.row?.accountType ?? state.defaultType);
  const [isActive, setIsActive] = useState(state.row?.isActive ?? true);
  const save = useMutation({
    meta: { successMessage: isEdit ? "Account updated" : "Account created" },
    mutationFn: () => isEdit
      ? accountsApi.updateAccount(state.row!.id, { name: name.trim(), isActive })
      : accountsApi.createAccount({ accountGroupId: state.groupId, code: code.trim(), name: name.trim(), accountType }),
    onSuccess: onSaved,
  });
  return (
    <Dialog open onClose={onClose} maxWidth="xs" fullWidth>
      <ClosableDialogTitle onClose={onClose}>{isEdit ? "Edit account" : "New account"}</ClosableDialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField label="Code *" size="small" value={code} onChange={e => setCode(e.target.value)}
            disabled={isEdit} helperText={isEdit ? "Code can't be changed — the ledger references it" : "Max 20 chars"} />
          <TextField label="Name *" size="small" value={name} onChange={e => setName(e.target.value)} autoFocus />
          <TextField select label="Type *" size="small" value={accountType} disabled={isEdit}
            onChange={e => setAccountType(e.target.value as AccountTypeName)}
            helperText={isEdit ? "Type is fixed after creation" : undefined}>
            {ACCOUNT_TYPES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
          </TextField>
          {isEdit && (
            <FormControlLabel control={<Switch checked={isActive} onChange={(_, v) => setIsActive(v)} />}
              label={isActive ? "Active" : "Inactive"} />
          )}
          {save.isError && <Alert severity="error">{errorMessage(save.error)}</Alert>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" disabled={save.isPending || !name.trim() || (!isEdit && !code.trim())}
          onClick={() => save.mutate()}>{isEdit ? "Save" : "Create"}</Button>
      </DialogActions>
    </Dialog>
  );
}

// ---- View account (read-only) ----
function ViewAccountDialog({ row, onClose }: { row: AccountBalanceRow | null; onClose: () => void }) {
  return (
    <Dialog open={!!row} onClose={onClose} maxWidth="xs" fullWidth>
      <ClosableDialogTitle onClose={onClose}>Account details</ClosableDialogTitle>
      {row && (
        <DialogContent>
          <Typography variant="h6" fontWeight={700}>{row.name}</Typography>
          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap sx={{ mt: 1.5 }}>
            <Field label="Code" value={row.code || "—"} mono />
            <Field label="Type" value={row.accountType} />
            <Field label="Status" value={row.isActive ? "Active" : "Inactive"} />
            <Field label="Debit" value={inr.format(row.debit)} />
            <Field label="Credit" value={inr.format(row.credit)} />
          </Stack>
        </DialogContent>
      )}
      <DialogActions><Button onClick={onClose}>Close</Button></DialogActions>
    </Dialog>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>{label}</Typography>
      <Typography variant="body2" sx={{ fontFamily: mono ? "monospace" : undefined }}>{value}</Typography>
    </Box>
  );
}
